import { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';

const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoadPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile && typeof window.turnstile.render === 'function') {
    return Promise.resolve();
  }
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => {
        scriptLoadPromise = null;
        reject(new Error('Turnstile script failed to load'));
      });
      return;
    }
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);

    // 10-second timeout to prevent endless spinner
    setTimeout(() => {
      if (scriptLoadPromise && !window.turnstile) {
        scriptLoadPromise = null;
        reject(new Error('Turnstile script load timeout'));
      }
    }, 10000);
  });
  return scriptLoadPromise;
}

/**
 * TurnstileWidget renders a Cloudflare Turnstile challenge.
 *
 * Props:
 *   - siteKey: string (required)
 *   - action: string (required, e.g. "register_otp", "reset_otp", "login")
 *   - onToken: (token) => void — called when a token is issued
 *   - onExpire: () => void — called when the token expires
 *   - onError: (error) => void — called on render/network errors
 *
 * Ref methods:
 *   - reset() — reset the widget so a new token can be obtained
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { siteKey, action, onToken, onExpire, onError },
  ref,
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [scriptError, setScriptError] = useState(false);

  useImperativeHandle(ref, () => ({
    reset() {
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
    },
  }));

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: action || undefined,
          callback: (token) => {
            if (onToken) onToken(token);
          },
          'expired-callback': () => {
            if (onExpire) onExpire();
          },
          'error-callback': (err) => {
            if (onError) onError(err);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setScriptError(true);
        if (onError) onError('script_load_failed');
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
    // We intentionally only re-render when siteKey changes. action is captured
    // at render time and changing it mid-session is unusual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;

  const handleRetry = () => {
    setScriptError(false);
    scriptLoadPromise = null;
    // Force re-render to trigger the useEffect again
    window.location.reload();
  };

  if (scriptError) {
    return (
      <div
        className="turnstile-error"
        style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}
      >
        خطا در بارگذاری تایید امنیت.{' '}
        <button
          type="button"
          onClick={handleRetry}
          style={{
            background: 'none',
            border: 'none',
            color: '#2563eb',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
            fontSize: 'inherit',
          }}
        >
          تلاش مجدد
        </button>
      </div>
    );
  }

  return <div ref={containerRef} className="turnstile-container" />;
});

export default TurnstileWidget;
